import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdScanner } from 'react-icons/md';
import { FiUpload, FiX, FiTrash2, FiSettings } from 'react-icons/fi';

// Advanced scanning UI leveraging Dynamsoft WebTWAIN if present.
// Features:
// - Select device
// - Color/BW, DPI, Paper size, Duplex
// - Suppress device UI and acquire into buffer
// - Live thumbnail list; delete/rotate/reorder
// - Define bookmark breakpoints with titles
// - Export PDF and upload; save bookmarks JSON alongside

const DPI_OPTIONS = [150, 200, 240, 300, 400, 600];
const COLOR_OPTIONS = [
  { label: 'Color', value: 'color' },
  { label: 'Grayscale', value: 'gray' },
  { label: 'Black & White', value: 'bw' },
];
const PAPER_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Letter (8.5x11)', value: 'letter' },
  { label: 'A4 (210x297mm)', value: 'a4' },
];

export default function AdvancedScanModal({ open, onClose, API_URL, fieldLabels = [], fieldKeys = [] }) {
  const [values, setValues] = useState(() => {
    try { return JSON.parse(localStorage.getItem('scan.fields') || '{}') || {}; } catch (_) { return {}; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [devices, setDevices] = useState([]);
  const [deviceIndex, setDeviceIndex] = useState(0);
  const [dpi, setDpi] = useState(() => {
    const v = parseInt(localStorage.getItem('scan.dpi') || '300', 10);
    return isNaN(v) ? 300 : v;
  });
  const [color, setColor] = useState(() => localStorage.getItem('scan.color') || 'color');
  const [paper, setPaper] = useState(() => localStorage.getItem('scan.paper') || 'auto');
  const [duplex, setDuplex] = useState(() => (localStorage.getItem('scan.duplex') !== '0'));

  const [thumbs, setThumbs] = useState([]); // [{idx, dataUrl}]
  const [bookmarks, setBookmarks] = useState([]); // [{pageIndex, title}]
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const triedDecodeRef = useRef(false); // first-page field autofill
  const sepDetectRef = useRef({ enabled: true, remove: true });
  const [detectSeparators, setDetectSeparators] = useState(() => (localStorage.getItem('scan.sep.detect') !== '0'));
  const [removeSeparators, setRemoveSeparators] = useState(() => (localStorage.getItem('scan.sep.remove') !== '0'));

  // Modal size and resizable split panes
  const initialW = Math.round((Number(localStorage.getItem('scan.modal.w')) || window.innerWidth * 0.86));
  const initialH = Math.round((Number(localStorage.getItem('scan.modal.h')) || window.innerHeight * 0.86));
  const [modalW, setModalW] = useState(initialW);
  const [modalH, setModalH] = useState(initialH);
  const [leftPct, setLeftPct] = useState(() => {
    const v = parseFloat(localStorage.getItem('scan.split.left') || '0.38');
    return isNaN(v) ? 0.38 : Math.min(0.75, Math.max(0.2, v));
  });
  const [stripW, setStripW] = useState(() => {
    const v = parseInt(localStorage.getItem('scan.strip.w') || '200', 10);
    return Math.min(420, Math.max(120, v));
  });
  const resizingRef = useRef(null); // { type: 'modal'|'left'|'strip', startX, startY, startW, startH, startPct, startStrip }

  const dwtRef = useRef(null);

  // Ensure a persistent hidden container exists for WebTWAIN so the object can live
  // across dialog open/close without being destroyed (improves device stability)
  const ensureDwtContainerId = () => {
    try {
      const id = 'spartaDwtContainer';
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('div');
        el.id = id;
        el.style.display = 'block';
        el.style.width = '1px';
        el.style.height = '1px';
        el.style.overflow = 'hidden';
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        el.style.top = '0';
        document.body.appendChild(el);
      }
      return id;
    } catch (_) {
      return 'spartaDwtContainer';
    }
  };

  useEffect(() => {
    if (!open) return;
    setError(''); setInfo(''); setBusy(false);
    // Clear filename fields each time the scan dialog is opened
    try { setValues({}); triedDecodeRef.current = false; } catch (_) {}
    // Try to enumerate devices
    try {
      const DWT = (window.Dynamsoft && window.Dynamsoft.DWT) || null;
      if (!DWT) return;
      const existing = (typeof window !== 'undefined' && window._spartaDWT) || null;
      const bindEventsOnce = (obj) => {
        if (!obj || obj._spartaEventsBound) return;
        const enumerateDevices = (attempt = 0) => {
          try {
            let names = (obj.GetSourceNames && obj.GetSourceNames()) || [];
            if (!names || names.length === 0) {
              // Some environments need extra ticks for the service to enumerate devices
              if (attempt < 5) {
                const delay = 250 + attempt * 250; // backoff up to ~1.5s
                return setTimeout(() => enumerateDevices(attempt + 1), delay);
              }
            }
            // Finalize
            names = Array.isArray(names) ? names : [];
            setDevices(names);
            // Restore preferred device by name if stored
            try {
              const pref = localStorage.getItem('scan.device') || '';
              if (pref && names.length) {
                const idx = names.findIndex(n => String(n) === pref);
                if (idx >= 0) setDeviceIndex(idx);
              }
            } catch (_) {}
          } catch (_) {}
        };
        enumerateDevices(0);
        // Expose a manual refresh helper on the instance
        try { obj._spartaRefreshDevices = () => enumerateDevices(0); } catch (_) {}
        try {
          // Refresh when transfers complete or error
          try { obj.RegisterEvent && obj.RegisterEvent('OnPostAllTransfers', () => { refreshThumbs(); }); } catch (_) {}
          try { obj.RegisterEvent && obj.RegisterEvent('OnTransferError', () => { refreshThumbs(); }); } catch (_) {}
          // Incremental update: after each page transfer, append/update last thumb + preview
          try {
            obj.RegisterEvent && obj.RegisterEvent('OnPostTransfer', () => {
              try {
                const o = dwtRef.current;
                const count = Number(o?.HowManyImagesInBuffer || 0);
                if (!count) return;
                const last = count - 1;
                convertToDataUrl(last).then((url) => {
                  if (!url) return;
                  setThumbs((prev) => {
                    const exists = prev.find((t) => t.idx === last);
                    if (exists) {
                      // Update the existing last thumb (some drivers re-scan)
                      return prev.map((t) => (t.idx === last ? { idx: last, dataUrl: url } : t));
                    }
                    return [...prev, { idx: last, dataUrl: url }];
                  });
                  setSelectedIdx(last);
                  setPreviewUrl(url);
                  // Decode for separator/fields on the fly
                  decodeBarcodeAt(last, url);
                });
              } catch (_) {}
            });
          } catch (_) {}
          obj._spartaEventsBound = true;
        } catch (_) {}
      };

      const containerId = ensureDwtContainerId();
      if (existing) {
        dwtRef.current = existing;
        bindEventsOnce(existing);
        if (typeof existing._spartaRefreshDevices === 'function') existing._spartaRefreshDevices();
      } else {
        DWT.CreateDWTObjectEx({ WebTwainId: 'sparta-dwt', ContainerId: containerId }, (obj) => {
          dwtRef.current = obj;
          try { window._spartaDWT = obj; } catch (_) {}
          bindEventsOnce(obj);
        }, () => {});
      }
    } catch (_) {}
    return () => {
      try {
        if (dwtRef.current) {
          try { dwtRef.current.CloseSource && dwtRef.current.CloseSource(); } catch (_) {}
          try { dwtRef.current.RemoveAllImages && dwtRef.current.RemoveAllImages(); } catch (_) {}
          // Keep the object alive to avoid re-init issues; do not Destroy here
        }
      } catch (_) {}
      // Keep global instance; just release our ref when dialog closes
      dwtRef.current = (typeof window !== 'undefined' && window._spartaDWT) || null;
      setThumbs([]);
      setBookmarks([]);
      setSelectedIdx(null);
      setPreviewUrl('');
    };
  }, [open]);

  // Persist field values
  useEffect(() => {
    try { localStorage.setItem('scan.fields', JSON.stringify(values || {})); } catch (_) {}
  }, [values]);

  // Persist simple options
  useEffect(() => { try { localStorage.setItem('scan.dpi', String(dpi)); } catch (_) {} }, [dpi]);
  useEffect(() => { try { localStorage.setItem('scan.color', String(color)); } catch (_) {} }, [color]);
  useEffect(() => { try { localStorage.setItem('scan.paper', String(paper)); } catch (_) {} }, [paper]);
  useEffect(() => { try { localStorage.setItem('scan.duplex', duplex ? '1' : '0'); } catch (_) {} }, [duplex]);
  useEffect(() => { try { localStorage.setItem('scan.sep.detect', detectSeparators ? '1' : '0'); } catch (_) {} }, [detectSeparators]);
  useEffect(() => { try { localStorage.setItem('scan.sep.remove', removeSeparators ? '1' : '0'); } catch (_) {} }, [removeSeparators]);
  // Persist device preference by name
  useEffect(() => {
    try {
      if (devices && devices.length && deviceIndex >= 0 && deviceIndex < devices.length) {
        localStorage.setItem('scan.device', String(devices[deviceIndex]));
      }
    } catch (_) {}
  }, [deviceIndex, devices]);

  const sanitizeFieldValue = (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    return trimmed.replace(/[\\/:*?"<>|]+/g, '');
  };

  const buildFilename = () => {
    const parts = fieldKeys.map((k) => sanitizeFieldValue(values[k]));
    const joined = parts.join('_');
    const base = joined.trim() ? joined : `scan_${Date.now()}`;
    return `${base}.pdf`;
  };

  const applyScanSettings = (obj) => {
    try {
      if (!obj) return;
      // Color mode
      if (color === 'color') obj.PixelType = 2; // RGB
      else if (color === 'gray') obj.PixelType = 1; // Gray
      else obj.PixelType = 0; // BW
      // DPI
      obj.Resolution = dpi;
      // Duplex
      try { obj.IfDuplexEnabled = !!duplex; } catch(_) {}
      // Paper (best effort; many drivers ignore this when feeder is used)
      if (paper === 'letter') {
        try { obj.PageSize = 1; } catch(_) {} // TWSS_USLETTER
      } else if (paper === 'a4') {
        try { obj.PageSize = 9; } catch(_) {} // TWSS_A4
      } else {
        try { obj.PageSize = 0; } catch(_) {} // TWSS_NONE/auto
      }
      // Feeder on (if available)
      try { obj.IfFeederEnabled = true; } catch(_) {}
      // Suppress device UI
      try { obj.IfShowUI = false; } catch(_) {}
    } catch (_) {}
  };

  const convertToDataUrl = (i) => new Promise((resolve) => {
    try {
      const obj = dwtRef.current; if (!obj) return resolve(null);
      // Try base64 first
      obj.ConvertToBase64(i, window.Dynamsoft.DWT.EnumDWT_ImageType.IT_JPG, (b64) => {
        const data = Array.isArray(b64) ? b64[0] : b64;
        resolve('data:image/jpeg;base64,' + data);
      }, async () => {
        // Fallback: blob -> dataURL
        try {
          obj.ConvertToBlob([i], window.Dynamsoft.DWT.EnumDWT_ImageType.IT_JPG, (blob) => {
            try {
              const fr = new FileReader();
              fr.onload = () => resolve(String(fr.result || ''));
              fr.onerror = () => resolve(null);
              fr.readAsDataURL(blob);
            } catch (_) { resolve(null); }
          }, () => resolve(null));
        } catch (_) { resolve(null); }
      });
    } catch (_) { resolve(null); }
  });

  const refreshThumbs = () => {
    try {
      const obj = dwtRef.current;
      if (!obj) return;
      const count = Number(obj.HowManyImagesInBuffer || 0);
      const indices = Array.from({ length: count }, (_, i) => i);
      if (indices.length === 0) { setThumbs([]); setSelectedIdx(null); setPreviewUrl(''); return; }
      Promise.all(indices.map(async (i) => {
        const url = await convertToDataUrl(i);
        return url ? { idx: i, dataUrl: url } : null;
      })).then((list) => {
        const filtered = list.filter(Boolean);
        setThumbs(filtered);
        const last = indices[indices.length - 1];
        const sel = (selectedIdx == null) ? last : selectedIdx;
        setSelectedIdx(sel);
        updatePreview(sel);
        // Attempt one-time barcode decode from first page
        if (!triedDecodeRef.current && indices.length > 0) {
          triedDecodeRef.current = true;
          decodeBarcodeAt(0);
        }
      });
    } catch (_) {}
  };

  const updatePreview = (i) => {
    try {
      const obj = dwtRef.current; if (!obj) return;
      if (typeof i !== 'number' || i < 0) { setPreviewUrl(''); return; }
      convertToDataUrl(i).then((url) => setPreviewUrl(url || ''));
    } catch (_) {}
  };

  const decodeBarcodeAt = async (i, preUrl = '') => {
    try {
      const url = preUrl || await convertToDataUrl(i);
      if (!url) return;
      const mod = await import('@zxing/browser');
      const reader = new mod.BrowserMultiFormatReader();
      const result = await reader.decodeFromImageUrl(url).catch(() => null);
      if (!result || !result.text) return;
      const text = String(result.text || '').trim();
      // Separator detection: pattern --BOOKMARK--
      const sep = text.match(/^--\s*([A-Za-z0-9 _-]{1,120})\s*--$/);
      if (sep) {
        const title = sep[1].trim();
        // Add/replace bookmark at this page
        setBookmarks(bm => [...bm.filter(b => b.pageIndex !== i), { pageIndex: i, title }].sort((a,b)=>a.pageIndex-b.pageIndex));
        setInfo(`Bookmark added: ${title}`);
        if (removeSeparators || sepDetectRef.current.remove) {
          // Remove separator sheet
          deletePage(i);
        }
        return;
      }

      // Parse strategies: JSON object, or key=value pairs, or delimited tokens for field autofill
      let parsed = {};
      try {
        if (/^\s*\{/.test(text)) parsed = JSON.parse(text);
      } catch (_) {}
      if (!Object.keys(parsed).length) {
        // Try key=value; separators ; | , _ or newline
        // Note: Server-generated barcodes use underscore-delimited values (values.join('_'))
        const parts = text.split(/[_|\n,_]+/).map(s => s.trim()).filter(Boolean);
        for (const p of parts) {
          const m = p.match(/^([^:=]+)[:=](.+)$/);
          if (m) parsed[m[1].trim().toLowerCase()] = m[2].trim();
        }
        // If still empty and parts count matches fields, map by order
        if (!Object.keys(parsed).length && fieldKeys.length && parts.length >= fieldKeys.length) {
          const vals = parts.slice(0, fieldKeys.length);
          parsed = fieldKeys.reduce((acc, k, idx) => { acc[k] = vals[idx] || ''; return acc; }, {});
        }
      }
      if (!Object.keys(parsed).length) return;
      // Normalize keys and apply
      const next = { ...values };
      for (const k of fieldKeys) {
        // try exact, then loose match by stripping non-alnum
        const v = parsed[k] ?? parsed[k?.toLowerCase()] ?? (() => {
          const target = k.toLowerCase().replace(/[^a-z0-9]+/g, '');
          for (const pk of Object.keys(parsed)) {
            const norm = String(pk).toLowerCase().replace(/[^a-z0-9]+/g, '');
            if (norm === target) return parsed[pk];
          }
          return undefined;
        })();
        if (typeof v === 'string') next[k] = v;
      }
      setValues(next);
      setInfo('Fields auto-filled from barcode.');
    } catch (_) {
      // ignore decode errors
    }
  };

  const acquire = async () => {
    setBusy(true); setError(''); setInfo('');
    try {
      const obj = dwtRef.current; if (!obj) throw new Error('Scanner not initialized');
      try { if (devices.length && obj.SelectSourceByIndex) obj.SelectSourceByIndex(deviceIndex); } catch (_) {}
      applyScanSettings(obj);
      try { obj.OpenSource && obj.OpenSource(); } catch (_) {}
      setInfo('Acquiring...');
      await new Promise((resolve, reject) => {
        try { obj.AcquireImage({}, () => resolve(), (c,m) => reject(new Error(m||String(c)))); } catch (e) { reject(e); }
      });
      try { obj.CloseSource && obj.CloseSource(); } catch (_) {}
      refreshThumbs();
      setInfo('Scan complete. Review pages below.');
    } catch (e) {
      const msg = String(e?.message || e || '').toLowerCase();
      if (msg.includes('cancel')) { setBusy(false); return; }
      setError(e?.message || 'Acquire failed');
    } finally { setBusy(false); }
  };

  const refreshDevicesNow = () => {
    try {
      const obj = dwtRef.current;
      if (obj && typeof obj._spartaRefreshDevices === 'function') obj._spartaRefreshDevices();
    } catch (_) {}
  };

  const openDriverSettings = async () => {
    setError('');
    setInfo('');
    let obj = null;
    let prevShowUI = null;
    let prevDisable = null;
    let prevCloseAfter = null;
    setBusy(true);
    try {
      obj = dwtRef.current;
      if (!obj) throw new Error('Scanner not initialized');
      try {
        if (devices.length && obj.SelectSourceByIndex) obj.SelectSourceByIndex(deviceIndex);
      } catch (_) {}
      try {
        prevShowUI = typeof obj.IfShowUI === 'boolean' ? obj.IfShowUI : null;
      } catch (_) { prevShowUI = null; }
      try {
        prevDisable = typeof obj.IfDisableSourceAfterAcquire === 'boolean' ? obj.IfDisableSourceAfterAcquire : null;
      } catch (_) { prevDisable = null; }
      try {
        prevCloseAfter = typeof obj.IfCloseSourceWhenAcquireFinished === 'boolean' ? obj.IfCloseSourceWhenAcquireFinished : null;
      } catch (_) { prevCloseAfter = null; }
      try { obj.IfShowUI = true; } catch (_) {}
      try { obj.IfDisableSourceAfterAcquire = true; } catch (_) {}
      try { obj.IfCloseSourceWhenAcquireFinished = true; } catch (_) {}
      await new Promise((resolve, reject) => {
        try {
          obj.AcquireImage({}, () => resolve(), (code, msg) => reject(new Error(msg || String(code))));
        } catch (e) { reject(e); }
      });
      setInfo('Driver settings dialog closed. You can now scan with the updated options.');
    } catch (e) {
      const msg = String(e?.message || e || '');
      if (msg.toLowerCase().includes('cancel')) {
        setInfo('Driver settings dialog closed.');
      } else {
        setError(e?.message || 'Failed to open driver settings');
      }
    } finally {
      try {
        if (obj && obj.CancelAllPendingTransfers) {
          try { obj.CancelAllPendingTransfers(); } catch (_) {}
        }
        if (obj) {
          try { obj.CloseSource && obj.CloseSource(); } catch (_) {}
          try { obj.RemoveAllImages && obj.RemoveAllImages(); } catch (_) {}
          if (prevShowUI !== null) {
            try { obj.IfShowUI = prevShowUI; } catch (_) {}
          } else {
            try { obj.IfShowUI = false; } catch (_) {}
          }
          if (prevDisable !== null) {
            try { obj.IfDisableSourceAfterAcquire = prevDisable; } catch (_) {}
          }
          if (prevCloseAfter !== null) {
            try { obj.IfCloseSourceWhenAcquireFinished = prevCloseAfter; } catch (_) {}
          }
        }
      } catch (_) {}
      setBusy(false);
    }
  };

  const deletePage = (i) => {
    try {
      const obj = dwtRef.current; if (!obj) return;
      obj.RemoveImage(i);
      refreshThumbs();
    } catch (_) {}
  };
  const rotatePage = (i, angle=90) => {
    try { const obj = dwtRef.current; if (!obj) return; obj.Rotate(i, angle, true); refreshThumbs(); } catch (_) {}
  };

  const addBookmarkAt = (i) => {
    const title = prompt('Bookmark title for page ' + (i+1));
    if (!title) return;
    setBookmarks(bm => [...bm.filter(b => b.pageIndex !== i), { pageIndex: i, title: title.trim() }].sort((a,b)=>a.pageIndex-b.pageIndex));
  };
  const removeBookmarkAt = (i) => setBookmarks(bm => bm.filter(b => b.pageIndex !== i));

  const clearSession = () => {
    try {
      setError('');
      setInfo('');
      triedDecodeRef.current = false;
      setValues({});
      setBookmarks([]);
      setSelectedIdx(null);
      setPreviewUrl('');
      const obj = dwtRef.current;
      if (obj) {
        try { obj.RemoveAllImages && obj.RemoveAllImages(); } catch (_) {}
        try { obj.CloseSource && obj.CloseSource(); } catch (_) {}
      }
      setThumbs([]);
      try { localStorage.removeItem('scan.fields'); } catch (_) {}
    } catch (_) {}
  };

  const exportAndUpload = async () => {
    setBusy(true); setError(''); setInfo('');
    try {
      const obj = dwtRef.current; if (!obj) throw new Error('Scanner not initialized');
      const count = Number(obj.HowManyImagesInBuffer || 0);
      if (!count) throw new Error('No pages to export');
      const indices = Array.from({ length: count }, (_, i) => i);
      const blob = await new Promise((resolve, reject) => {
        try {
          obj.ConvertToBlob(indices, window.Dynamsoft.DWT.EnumDWT_ImageType.IT_PDF, (res) => resolve(res), (c,m)=>reject(new Error(m||String(c))));
        } catch (e) { reject(e); }
      });
      const filename = buildFilename();
      // Upload PDF
      const form = new FormData();
      form.append('pdf', blob, filename);
      const upRes = await fetch(`${API_URL}/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')||''}` }, body: form });
      if (!upRes.ok) {
        const j = await upRes.json().catch(()=>({}));
        throw new Error(j.message || 'Upload failed');
      }
      // Upload bookmarks JSON sidecar (optional)
      if (bookmarks.length) {
        await fetch(`${API_URL}/files/${encodeURIComponent(filename)}/bookmarks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')||''}` },
          body: JSON.stringify({ bookmarks }),
        }).catch(()=>{});
      }
      // Ask to download the scanned PDF now (simple browser download)
      try {
        const want = window.confirm('Upload complete. Download a copy of the PDF now?');
        if (want) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        }
      } catch (_) {}
      setInfo('Uploaded successfully.');
      onClose && onClose(true);
    } catch (e) {
      setError(e?.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="upload-spinner-overlay" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="upload-spinner-box" style={{ width: modalW, height: modalH, maxWidth: '98vw', maxHeight: '98vh', textAlign: 'left', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        <h3 style={{ marginTop: 0 }}>Advanced Scan</h3>
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left controls pane */}
          <div style={{ width: `${Math.round(leftPct * 100)}%`, minWidth: 280, paddingRight: 8, overflow: 'auto', borderRight: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <label>Device</label><br />
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select value={deviceIndex} onChange={e => setDeviceIndex(Number(e.target.value))}>
                    {devices.length === 0 && <option value={-1}>No devices found</option>}
                    {devices.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                  <button className="btn btn-ghost" type="button" title="Refresh devices" onClick={refreshDevicesNow}>↻</button>
                </div>
                {devices.length === 0 && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    Scanner not detected. Ensure Dynamsoft Service is running, then click Refresh.
                  </div>
                )}
              </div>
              <div>
                <label>DPI</label><br />
                <select value={dpi} onChange={e => setDpi(Number(e.target.value))}>
                  {DPI_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label>Color</label><br />
                <select value={color} onChange={e => setColor(e.target.value)}>
                  {COLOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label>Paper</label><br />
                <select value={paper} onChange={e => setPaper(e.target.value)}>
                  {PAPER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ alignSelf: 'end' }}>
                <label><input type="checkbox" checked={duplex} onChange={e => setDuplex(e.target.checked)} /> Duplex</label>
              </div>
              <div style={{ width: '100%', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <label title="Detect --BOOKMARK-- barcode on any page">
                    <input type="checkbox" checked={detectSeparators} onChange={e => { setDetectSeparators(e.target.checked); sepDetectRef.current.enabled = e.target.checked; }} /> Detect bookmark separators
                  </label>
                  <label title="Remove the separator sheet from the output">
                    <input type="checkbox" checked={removeSeparators} onChange={e => { setRemoveSeparators(e.target.checked); sepDetectRef.current.remove = e.target.checked; }} /> Remove separator pages
                  </label>
                  <span style={{ fontSize: 12, color: '#666' }}>Format: <code>--BOOKMARK NAME--</code></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn btn-ghost" type="button" disabled={busy} onClick={openDriverSettings} title="Open the scanner driver's advanced settings">
                    <FiSettings />
                    <span style={{ marginLeft: 6 }}>Driver Settings</span>
                  </button>
                  <button className="btn btn-ghost" disabled={busy} onClick={clearSession} title="Clear images and filename fields" style={{ whiteSpace: 'nowrap' }}>
                    <FiTrash2 />
                    <span style={{ marginLeft: 6 }}>Clear</span>
                  </button>
                  <button className="btn" disabled={busy} onClick={acquire} title="Scan pages using the selected settings" style={{ whiteSpace: 'nowrap' }}>
                    <MdScanner />
                    <span style={{ marginLeft: 6 }}>Scan</span>
                  </button>
                </div>
              </div>
            </div>

            {/* WebTWAIN uses a persistent hidden container attached to document.body */}

            <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 'bold' }}>Pages: {thumbs.length}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {thumbs.length === 0 && <div style={{ color: 'var(--text)' }}>No pages scanned yet.</div>}
                {thumbs.map(t => (
                  <div key={t.idx} style={{ border: '1px solid var(--border)', borderRadius: 4, padding: 6, background: 'var(--card-bg)' }}>
                    <img src={t.dataUrl} alt={`p${t.idx+1}`} style={{ width: 120, height: 'auto', display: 'block' }} />
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Page {t.idx + 1}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'space-between' }}>
                      <button className="btn-ghost" title="Rotate 90° clockwise" aria-label="Rotate page" onClick={() => rotatePage(t.idx, 90)}>↻</button>
                      <button className="btn-ghost" title="Delete this page" aria-label="Delete page" onClick={() => deletePage(t.idx)}>🗑️</button>
                      <button className="btn-ghost" title="Add bookmark at this page" aria-label="Add bookmark" onClick={() => addBookmarkAt(t.idx)}>🔖</button>
                    </div>
                    {bookmarks.some(b => b.pageIndex === t.idx) && (
                      <div style={{ marginTop: 4, fontSize: 12 }}>
                        Bookmark: {bookmarks.find(b => b.pageIndex === t.idx)?.title}
                        <button className="btn-ghost" style={{ marginLeft: 6 }} onClick={() => removeBookmarkAt(t.idx)}>✕</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Splitter between controls and preview side */}
          <div
            onMouseDown={(e) => {
              resizingRef.current = { type: 'left', startX: e.clientX, startPct: leftPct };
              const onMove = (ev) => {
                if (!resizingRef.current) return;
                const delta = ev.clientX - resizingRef.current.startX;
                const next = Math.min(0.75, Math.max(0.2, resizingRef.current.startPct + (delta / modalW)));
                setLeftPct(next);
              };
              const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                localStorage.setItem('scan.split.left', String(leftPct));
                resizingRef.current = null;
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
            style={{ width: 6, cursor: 'col-resize', background: 'var(--hover-bg)' }}
          />
          {/* Right preview pane: strip + large preview */}
          <div style={{ flex: 1, minWidth: 240, display: 'flex', minHeight: 0 }}>
            {/* Thumbnail strip */}
            <div style={{ width: stripW, minWidth: 120, maxWidth: 420, borderRight: '1px solid var(--border)', overflowY: 'auto' }}>
              {thumbs.length === 0 && <div style={{ padding: 8, color: 'var(--text)' }}>No pages</div>}
              {thumbs.map(t => (
                <div key={t.idx} onClick={() => { setSelectedIdx(t.idx); updatePreview(t.idx); }} style={{ padding: 6, borderBottom: '1px solid var(--border)', background: t.idx === selectedIdx ? 'var(--row-selected-bg)' : 'transparent', cursor: 'pointer' }}>
                  <img src={t.dataUrl} alt={`p${t.idx+1}`} style={{ width: '100%', height: 'auto', display: 'block' }} />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Page {t.idx + 1}</div>
                </div>
              ))}
            </div>
            {/* Strip splitter */}
            <div
              onMouseDown={(e) => {
                resizingRef.current = { type: 'strip', startX: e.clientX, startStrip: stripW };
                const onMove = (ev) => {
                  if (!resizingRef.current) return;
                  const next = Math.min(420, Math.max(120, resizingRef.current.startStrip + (ev.clientX - resizingRef.current.startX)));
                  setStripW(next);
                };
                const onUp = () => {
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                  localStorage.setItem('scan.strip.w', String(stripW));
                  resizingRef.current = null;
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
              style={{ width: 6, cursor: 'col-resize', background: 'var(--hover-bg)' }}
            />
            {/* Large preview */}
            <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', background: 'var(--card-bg)' }}>
              {previewUrl ? (
                <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ color: '#666' }}>Select a page to preview</div>
              )}
            </div>
          </div>
        </div>
        {/* Right-bottom actions side */}
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 'bold' }}>Filename Fields</div>
            {fieldKeys.map((k, i) => (
              <div key={k} style={{ marginTop: 6 }}>
                <label style={{ fontSize: 12 }}>{fieldLabels[i] || k}</label>
                <input type="text" value={values[k] || ''} onChange={e => setValues(v => ({ ...v, [k]: e.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-soft)', background: 'var(--input-bg)', color: 'var(--text)' }} />
              </div>
            ))}
            <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>Preview name: <code>{buildFilename()}</code></div>
          </div>

          {error && <div style={{ background: '#fdecea', color: '#b71c1c', padding: 8, borderRadius: 4, marginBottom: 8 }}>{error}</div>}
          {info && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: 8, borderRadius: 4, marginBottom: 8 }}>{info}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <button className="btn" disabled={busy || thumbs.length === 0} onClick={exportAndUpload} title="Save pages as a PDF and upload to the cabinet">
              <FiUpload />
              <span style={{ marginLeft: 6 }}>Save & Upload</span>
            </button>
            <button className="btn btn-ghost" disabled={busy} onClick={() => onClose && onClose(false)} title="Close advanced scan">
              <FiX />
              <span style={{ marginLeft: 6 }}>Close</span>
            </button>
          </div>
        </div>

        {/* Resize handle */}
        <div
          onMouseDown={(e) => {
            resizingRef.current = { type: 'modal', startX: e.clientX, startY: e.clientY, startW: modalW, startH: modalH };
            const onMove = (ev) => {
              if (!resizingRef.current) return;
              const nextW = Math.min(window.innerWidth * 0.98, Math.max(720, resizingRef.current.startW + (ev.clientX - resizingRef.current.startX)));
              const nextH = Math.min(window.innerHeight * 0.98, Math.max(520, resizingRef.current.startH + (ev.clientY - resizingRef.current.startY)));
              setModalW(nextW); setModalH(nextH);
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
              localStorage.setItem('scan.modal.w', String(modalW));
              localStorage.setItem('scan.modal.h', String(modalH));
              resizingRef.current = null;
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
          title="Drag to resize"
          style={{ position: 'absolute', right: 4, bottom: 4, width: 16, height: 16, cursor: 'nwse-resize', borderRight: '2px solid var(--border)', borderBottom: '2px solid var(--border)' }}
        />
      </div>
    </div>
  );
}
